/**
 * ---------------------------------------------------------------------------------------------------------------------
 * FRG BACK TO TOP
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * <frg-back-to-top> custom element (markup: snippets/frg-back-to-top.liquid, styles: assets/frg-back-to-top.css).
 *
 * - Visibility uses ONE IntersectionObserver on a sentinel covering the first N pixels of the document. There is no
 *   scroll listener, so scrolling costs no JavaScript, and pages whose height changes after load (infinite scroll,
 *   lazy sections, product recommendations, app widgets) need no special handling: the sentinel never moves.
 * - Scrolls smoothly, unless the visitor asked for reduced motion.
 * - When activated from the keyboard / assistive technology, focus is moved to the first focusable element of the page
 *   (the theme's "Skip to content" link), so keyboard users are not left on a button that is being hidden.
 * - The hidden state uses visibility:hidden, which removes the button from the tab order and the accessibility tree.
 */

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

class FrgBackToTop extends HTMLElement {
  #abortController = null;
  #observer = null;

  connectedCallback() {
    this.#abortController = new AbortController();

    const sentinel = this.querySelector('.frg-back-to-top__sentinel');
    this.button = this.querySelector('.frg-back-to-top__button');

    if (!sentinel || !this.button) {
      return;
    }

    this.#observer = new IntersectionObserver(this.#onIntersection.bind(this));
    this.#observer.observe(sentinel);

    this.button.addEventListener('click', this.#onClick.bind(this), { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #onIntersection(entries) {
    // The sentinel starts at the very top of the document, so it can only leave the viewport through its top edge.
    const entry = entries[entries.length - 1];
    this.classList.toggle('is-visible', !entry.isIntersecting);
  }

  #onClick(event) {
    window.scrollTo({ top: 0, behavior: reducedMotionQuery.matches ? 'auto' : 'smooth' });

    // event.detail is 0 for keyboard / assistive technology activation (Enter, Space) and >= 1 for pointer clicks.
    if (event.detail === 0) {
      const firstFocusable = document.querySelector('.skip-to-content, header a[href], header button');
      firstFocusable?.focus({ preventScroll: true });
    }
  }
}

if (!window.customElements.get('frg-back-to-top')) {
  window.customElements.define('frg-back-to-top', FrgBackToTop);
}
